import Button from '@carvana/ds-button';
import Carousel from '@carvana/ds-carousel';
import { CheckCircleSpot, StarFilledCvna, ZapFilledCvna } from '@carvana/ds-icons';
import List from '@carvana/ds-list';
import { icon } from '@carvana/ds-styles';
import cx from 'classnames';
import { Check } from 'lucide-react';
import React from 'react';
import EventedModal from '|/app/modals/evented-modal';
import { ModalNames } from '|/app/modals/modal-events';
import { StepName } from '|/app/services';
import { buildFinancingImpactStripText } from '|/helpers/carvana-care-modal-variants/carvana-care-modal-variants';
import currency from '|/helpers/currency';
import useNavigateToStep from '|/hooks/use-navigate-to-step';
import styles from './carvana-care-modal.module.css';
import getCoverageFeatures, { getFuelType, FUEL_SPECIFIC_ADDITIONS } from './get-coverage-features';

export interface CarvanaCareModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  coverageType: 'Essential' | 'Cosmetic' | 'Plus' | 'Premier';
  coverageDetails: {
    duration: string;
    mileage: string;
  };
  fuelDescription?: string;
  showSocialProof?: boolean;
  impactsFinancingTerms?: boolean;
  variant?: 'default' | 'treatment';
  vehicleModel?: string;
  monthlyDelta?: number;
  downDelta?: number;
  termDelta?: number;
  /** See `deltasBetweenLoans` — false means the deltas are placeholders, not real zeros. */
  hasLoanComparison?: boolean;
  /**
   * pp-check-it-out only: re-prices the cost row.
   * Deliberately separate from `variant`, which the CC/GAP recovery experiment also sets to
   * `treatment` from the control review-order files — those customers must keep the original
   * cost row, so the two cannot share a switch.
   */
  useSnapshotPricing?: boolean;
  fullPrice?: number;
  isFinanced?: boolean;
}

interface CarvanaCareReview {
  author: string;
  rating: number;
  text: string;
  location?: string;
  vehicle?: string;
}

/**
 * Per-plan social-proof reviews, sourced from the Figma design (TXCC-9612).
 * Essential is intentionally omitted — `shouldShowReviews` already gates the
 * carousel off for Essential customers via the `coverageType !== 'Essential'`
 * check.
 */
const SOCIAL_PROOF_REVIEWS_BY_PLAN: Record<
  Exclude<CarvanaCareModalProps['coverageType'], 'Essential'>,
  CarvanaCareReview[]
> = {
  Cosmetic: [
    {
      author: 'Justin W.',
      rating: 5,
      text: 'Fantastic experience! Truly happy to have the added peace of mind!',
      location: 'FL',
      vehicle: '2023 Audi Q4 e-tron Sportback'
    },
    {
      author: 'Jana K.',
      rating: 5,
      text: "The claims professional Glenn was understanding, professional and went above and beyond to rectify what could have been very precarious situation. I'm grateful.",
      location: 'NY',
      vehicle: '2020 Kia Sportage'
    },
    {
      author: 'David A.',
      rating: 5,
      text: 'Felt like I was very fairly treated and received the value I paid for. Thank you.',
      location: 'OH',
      vehicle: '2018 Jeep Wrangler Unlimited'
    }
  ],
  Plus: [
    {
      author: 'Gene K.',
      rating: 5,
      text: 'In all my years dealing with various warranty companies…. You by far are the absolute best!!!!!',
      location: 'IN',
      vehicle: '2014 Acura RDX'
    },
    {
      author: 'Richard E.',
      rating: 5,
      text: 'Was very happy to have the extended warranty it saved me paying thousands of dollars.',
      location: 'NY',
      vehicle: '2018 Dodge Journey'
    },
    {
      author: 'Leslie T.',
      rating: 5,
      text: "I was really hesitant about purchasing this extended warranty. At 61, you tend to be skeptical. Turns out, it was the absolute best decision I could've made! Thank you for being a positive role in this experience!",
      location: 'AL',
      vehicle: '2018 Toyota Highlander'
    }
  ],
  Premier: [
    {
      author: 'Jeffrey M.',
      rating: 5,
      text: 'The next vehicle I purchase WILL BE from Carvana and I most certainly will opt in for the warranty. It has provided much assistance and extreme peace of mind.',
      location: 'NC',
      vehicle: '2018 Lincoln Navigator'
    },
    {
      author: 'David G.',
      rating: 5,
      text: 'I’m so glad that I chose to get this extended coverage! The shop was amazing the claims process was simple and my deductible was even better!!!!',
      location: 'NM',
      vehicle: '2012 Ford F-150'
    },
    {
      author: 'Tierra B.',
      rating: 5,
      text: 'The process was seamless and the repair facility did a great job! 😀',
      location: 'MS',
      vehicle: '2019 Nissan Versa'
    }
  ]
};

const TESTIMONIALS_ATTRIBUTION =
  'Testimonials reference their experience with the service contract administrator, SilverRock Inc and are sourced from them.';

const formatWholeDollars = (value: number) =>
  currency(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const formatSnapshotCoverageTerm = ({
  duration,
  mileage
}: CarvanaCareModalProps['coverageDetails']) => {
  const compactDuration = duration.replace(/\s+months?$/i, ' mo.');
  const mileageMatch = mileage.match(/^([+-]?)([\d,]+)\s+miles?$/i);

  if (!mileageMatch) {
    return `${compactDuration} or ${mileage.replace(/\s+miles?$/i, ' mi.')}`;
  }

  const miles = Number(mileageMatch[2].replaceAll(',', ''));
  const compactMileage =
    Number.isFinite(miles) && miles % 1000 === 0
      ? `${mileageMatch[1]}${miles / 1000}k mi.`
      : `${mileageMatch[1]}${mileageMatch[2]} mi.`;

  return `${compactDuration} or ${compactMileage}`;
};

const CarvanaCareModal = ({
  isOpen,
  setIsOpen,
  coverageType,
  coverageDetails,
  fuelDescription,
  showSocialProof = false,
  impactsFinancingTerms = false,
  variant = 'default',
  vehicleModel,
  monthlyDelta = 0,
  downDelta = 0,
  termDelta = 0,
  hasLoanComparison = false,
  useSnapshotPricing = false,
  fullPrice = 0,
  isFinanced = true
}: CarvanaCareModalProps) => {
  const navigate = useNavigateToStep();
  const fuelType = getFuelType(fuelDescription);
  const features = getCoverageFeatures(coverageType, fuelType, useSnapshotPricing);
  const isTreatment = variant === 'treatment';
  const shouldShowReviews = showSocialProof && coverageType !== 'Essential';
  const coverageTerm =
    coverageType === 'Cosmetic'
      ? coverageDetails.duration
      : useSnapshotPricing
        ? formatSnapshotCoverageTerm(coverageDetails)
        : `${coverageDetails.duration} or ${coverageDetails.mileage}`;

  /**
   * The monthly framing only holds when the plan is absorbed entirely by the loan. Once it
   * moves the down payment, the customer pays part of it upfront, so the card switches to the
   * plan's full price — the same thing cash and SAF customers see.
   *
   * `hasLoanComparison` is required, not defensive: without a baseline loan every delta is 0,
   * which would otherwise read as "adds nothing" and render "$0/mo".
   */
  const showAsMonthly = useSnapshotPricing
    ? isFinanced && hasLoanComparison && downDelta === 0
    : isFinanced;

  /**
   * The design renders the delta as "+$26/mo" on Essential but "$26/mo" on Plus,
   * and GAP omits it too. Reproduced as drawn per design's call.
   */
  const showMonthlyPlusSign = useSnapshotPricing ? coverageType === 'Essential' : true;

  // Rendered into whichever cost line the active design puts it on — see the cost card below.
  const costAmount = (
    <div className={styles.treatmentCostAmount}>
      {showAsMonthly ? (
        <>
          <span
            className={styles.treatmentCostAmountStrong}
            data-testid="carvana-care-monthly-price"
          >
            {showMonthlyPlusSign && '+'}
            {formatWholeDollars(monthlyDelta)}
          </span>
          <span className={styles.treatmentCostAmountSuffix}>/mo</span>
        </>
      ) : (
        <span className={styles.treatmentCostAmountStrong} data-testid="carvana-care-full-price">
          {formatWholeDollars(fullPrice)}
        </span>
      )}
    </div>
  );

  const modalEventMetadata: Record<string, unknown> = {
    product: 'CarvanaCare',
    coverageType,
    ...(isTreatment
      ? showAsMonthly
        ? { monthlyPrice: monthlyDelta, downPayment: downDelta, termDelta }
        : { fullPrice }
      : {})
  };

  const isElectricOrHybridFeature = (feature: string): boolean => {
    const evHybridFeatures = [
      ...FUEL_SPECIFIC_ADDITIONS.electric,
      ...FUEL_SPECIFIC_ADDITIONS.hybrid
    ];
    return evHybridFeatures.includes(feature);
  };

  const renderFeaturesList = ({
    includeCancelAnytime = false
  }: { includeCancelAnytime?: boolean } = {}) => (
    <div className={styles.featuresSection}>
      <List
        variant="icon"
        size="small"
        className={cx(styles.featuresList, useSnapshotPricing && styles.snapshotFeaturesList)}
        testId="carvana-care-features-list"
        items={[
          ...features.map(feature => {
            const isEvHybridFeature = isElectricOrHybridFeature(feature);
            const IconComponent = isEvHybridFeature ? ZapFilledCvna : Check;
            const iconTestId = isEvHybridFeature ? 'zap-icon' : 'check-icon';
            const featureId = feature.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();

            return {
              key: featureId,
              icon: (
                <IconComponent
                  size={16}
                  color={icon.primary}
                  className={styles.checkIcon}
                  data-testid={iconTestId}
                />
              ),
              content: (
                <span
                  className={styles.featureText}
                  data-testid={`carvana-care-feature-${featureId}`}
                >
                  {feature}
                </span>
              )
            };
          }),
          ...(includeCancelAnytime
            ? [
                {
                  key: 'cancel-anytime',
                  icon: (
                    <Check
                      size={16}
                      color={icon.primary}
                      className={styles.checkIcon}
                      data-testid="check-icon"
                    />
                  ),
                  content: (
                    <span
                      className={styles.featureText}
                      data-testid="carvana-care-feature-cancel-anytime"
                    >
                      <strong>Cancel anytime</strong> for a prorated refund*
                    </span>
                  )
                }
              ]
            : [])
        ]}
      />
      {includeCancelAnytime && (
        <p
          className={cx(styles.footnote, useSnapshotPricing && styles.snapshotFootnote)}
          data-testid="carvana-care-footnote"
        >
          *See CarvanaCare contract for details.
        </p>
      )}
    </div>
  );

  return (
    <EventedModal
      modalHeader="CarvanaCare®"
      mobileStyle="inset"
      desktopWidth="480px"
      divLine={false}
      onClose={() => {
        setIsOpen(false);
      }}
      isOpen={isOpen}
      analyticsId={ModalNames.CarvanaCare}
      contentPadding={false}
      eventMetadata={modalEventMetadata}
    >
      <div
        className={cx(styles.container, useSnapshotPricing && styles.snapshotContainer)}
        data-testid="carvana-care-modal-container"
      >
        <div className={styles.content}>
          {isTreatment ? (
            <>
              <div className={styles.treatmentHeader}>
                <CheckCircleSpot
                  size={32}
                  color={icon.primary}
                  className={styles.treatmentHeaderIcon}
                  data-testid="carvana-care-treatment-header-icon"
                />
                <h2
                  className={styles.treatmentHeaderTitle}
                  data-testid="carvana-care-treatment-heading"
                >
                  Your {vehicleModel ?? 'vehicle'} is protected with CarvanaCare® {coverageType}
                </h2>
              </div>

              <div className={styles.treatmentCostCard} data-testid="carvana-care-cost-card">
                <div className={styles.treatmentCostRow}>
                  {/* Both lines are label-plus-amount rows so the price can sit on either one.
                      The design baseline-aligns it with the term on the second line; the
                      recovery experiment's card keeps it up beside "Covers you for". */}
                  <div className={styles.treatmentCostLine}>
                    <div className={styles.treatmentCostLabelPrimary}>Covers you for</div>
                    {!useSnapshotPricing && costAmount}
                  </div>
                  <div className={styles.treatmentCostLine}>
                    <div className={styles.treatmentCostLabelSecondary}>{coverageTerm}</div>
                    {useSnapshotPricing && costAmount}
                  </div>
                </div>
                {/* Absent from all seven snapshot frames, and it would be wrong there anyway:
                    `showAsMonthly` already implies `downDelta === 0`, so the strip could only
                    ever announce "adds $0 to your down payment". */}
                {!useSnapshotPricing && showAsMonthly && impactsFinancingTerms && (
                  <div
                    className={styles.treatmentTermsChanged}
                    data-testid="carvana-care-treatment-terms-changed"
                  >
                    {buildFinancingImpactStripText({ downDelta, termDelta })}
                  </div>
                )}
              </div>

              {renderFeaturesList({ includeCancelAnytime: true })}

              {shouldShowReviews && (
                <div
                  className={styles.reviewsCarouselSection}
                  data-testid="carvana-care-reviews-section"
                >
                  <Carousel
                    ariaLabel="CarvanaCare customer reviews"
                    itemGap="sm"
                    classes={{
                      wrapper: styles.reviewsCarousel,
                      topSection: styles.reviewsCarouselTopSection,
                      arrows: styles.reviewsCarouselArrows
                    }}
                  >
                    {(SOCIAL_PROOF_REVIEWS_BY_PLAN[coverageType] ?? []).map(review => (
                      <div
                        key={review.author}
                        className={styles.reviewCard}
                        data-testid={`carvana-care-review-${review.author
                          .replace(/[^a-zA-Z0-9]/g, '-')
                          .toLowerCase()}`}
                      >
                        <span
                          className={styles.reviewCardRating}
                          aria-label={`${review.rating} out of 5 stars`}
                          data-testid="carvana-care-review-rating"
                        >
                          {Array.from({ length: review.rating }).map((_, index) => (
                            <StarFilledCvna
                              key={index}
                              size={16}
                              color={icon.warning}
                              data-testid="carvana-care-review-star"
                            />
                          ))}
                        </span>
                        <div className={styles.reviewCardAuthorRow}>
                          <span className={styles.reviewCardAuthor}>{review.author}</span>
                          {review.location && (
                            <span className={styles.reviewCardLocation}>
                              from {review.location}
                            </span>
                          )}
                        </div>
                        {review.vehicle && (
                          <p className={styles.reviewCardVehicle}>{review.vehicle}</p>
                        )}
                        <p className={styles.reviewCardText}>{review.text}</p>
                      </div>
                    ))}
                  </Carousel>
                  <p
                    className={styles.reviewsAttribution}
                    data-testid="carvana-care-reviews-attribution"
                  >
                    {TESTIMONIALS_ATTRIBUTION}
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              <div className={styles.coverageSection}>
                <div className={styles.headline}>
                  <h2 className={styles.title}>
                    {(fuelType === 'electric' || fuelType === 'hybrid') && (
                      <ZapFilledCvna
                        size={16}
                        color={icon.primary}
                        className={styles.titleIcon}
                        data-testid="title-zap-icon"
                      />
                    )}
                    {coverageType}
                  </h2>
                </div>
                <h3
                  className={styles.coverageDescription}
                  data-testid="carvana-care-coverage-description"
                >
                  You&apos;re covered with CarvanaCare {coverageType} for{' '}
                  <strong>
                    {coverageType === 'Cosmetic'
                      ? coverageDetails.duration
                      : `${coverageDetails.duration} or ${coverageDetails.mileage}`}
                  </strong>
                </h3>
              </div>

              {renderFeaturesList()}
            </>
          )}
        </div>

        <div className={isTreatment ? styles.actionsTreatment : styles.actions}>
          <Button
            variant="tertiary"
            size="md"
            {...(isTreatment ? { width: '100%' } : {})}
            data-testid="edit-carvana-care-button"
            data-analytics-id="pp_carvana_care_modal_edit"
            onClick={() => {
              navigate(StepName.VSC);
            }}
          >
            Edit
          </Button>
          <Button
            variant="primary"
            size="md"
            {...(isTreatment ? { width: '100%' } : {})}
            data-testid="close-carvana-care-button"
            data-analytics-id="pp_carvana_care_modal_close"
            onClick={() => {
              setIsOpen(false);
            }}
          >
            Close
          </Button>
        </div>
      </div>
    </EventedModal>
  );
};

export default CarvanaCareModal;
